import HardBreak from '@tiptap/extension-hard-break';
import {
  Fragment,
  type Mark,
  type NodeType,
  type Node as PMNode,
} from '@tiptap/pm/model';
import { type EditorState, TextSelection, type Transaction } from '@tiptap/pm/state';

/**
 * Structural contexts whose paragraphs serialize as standalone blocks, so an
 * empty paragraph persists as the `&nbsp;` marker
 * (`paragraph-markdown.ts`'s round-trip-safe blank-line vocabulary). Table
 * cells are deliberately excluded — a cell's paragraphs serialize into one
 * compact GFM row, where the marker vocabulary is unproven — and headings
 * can't parent a paragraph at all, so they never reach the check.
 */
const NORMALIZABLE_PARENT_NAMES: Record<string, true> = {
  doc: true,
  sectionNode: true,
  listItem: true,
  blockquote: true,
};

/** Pure result of {@link regroupParagraphAtCursor}. */
export interface HardBreakLineRegroupResult {
  /** Replacement paragraph nodes, in order. */
  blocks: PMNode[];
  /** Index into `blocks` that receives the cursor, at its content start. */
  cursorBlockIndex: number;
}

/**
 * A `setHardBreak` invocation that would create a break-only line — the
 * degenerate shape marked's block lexer collapses on the next parse —
 * resolved into a paragraph-level rewrite. `from`/`to` bound
 * the paragraph being replaced (`$from.before()`/`$from.after()`).
 */
export interface HardBreakLineNormalizationPlan extends HardBreakLineRegroupResult {
  from: number;
  to: number;
}

/**
 * Splits `content` into "lines" at each `hardBreak` child: N breaks always
 * produce N + 1 fragments, so empty content still yields its single empty
 * line. Parallels `sections/split-content-at-line-break.ts`'s walk idiom but
 * stays independent — that helper splits at the *first* break only and also
 * treats a literal `\n` as a break (the unrelated soft-break vocabulary);
 * neither generalizes to this multi-split, hardBreak-only need.
 */
const splitIntoLines = (content: Fragment, hardBreakType: NodeType): Fragment[] => {
  const lines: Fragment[] = [];
  let lineStart = 0;
  let offset = 0;
  content.forEach((node) => {
    if (node.type === hardBreakType) {
      lines.push(content.cut(lineStart, offset));
      lineStart = offset + node.nodeSize;
    }
    offset += node.nodeSize;
  });
  lines.push(content.cut(lineStart));
  return lines;
};

/** Rejoins a run of lines with one `hardBreak` between each pair. */
const joinLinesWithHardBreaks = (lines: Fragment[], hardBreakType: NodeType): Fragment =>
  lines.reduce(
    (joined, line, index) =>
      index === 0
        ? line
        : joined.append(Fragment.from(hardBreakType.create())).append(line),
    Fragment.empty
  );

/**
 * Pure core of the hard-break normalization. Drops the selected range (empty
 * for a plain cursor), splits the remaining inline content into hardBreak-delimited
 * lines with the one line boundary a hardBreak insertion at the selection
 * would have created, and regroups the result into blocks: a maximal run of
 * non-empty lines stays one paragraph joined by hardBreaks (a genuine
 * mid-line break is preserved untouched), while every empty line — the shape
 * that collapses on re-parse — becomes its own empty paragraph.
 *
 * `cursorBlockIndex` names the paragraph whose content start should receive
 * the cursor. The caller only invokes this when the split point sits on a
 * break-only seam, which forces the cursor's line to be either a lone empty
 * paragraph or the first line of the run it joins — so "start of that
 * paragraph" is always where the insertion would have left the cursor.
 */
export const regroupParagraphAtCursor = (
  content: Fragment,
  fromOffset: number,
  toOffset: number,
  paragraphType: NodeType,
  hardBreakType: NodeType
): HardBreakLineRegroupResult => {
  const beforeLines = splitIntoLines(content.cut(0, fromOffset), hardBreakType);
  const afterLines = splitIntoLines(content.cut(toOffset), hardBreakType);
  const lines = [...beforeLines, ...afterLines];
  const cursorLineIndex = beforeLines.length;

  const blocks: PMNode[] = [];
  let cursorBlockIndex = 0;
  let runStart = 0;

  const flushRun = (end: number): void => {
    if (end <= runStart) return;
    blocks.push(
      paragraphType.create(
        null,
        joinLinesWithHardBreaks(lines.slice(runStart, end), hardBreakType)
      )
    );
    if (cursorLineIndex >= runStart && cursorLineIndex < end) {
      cursorBlockIndex = blocks.length - 1;
    }
  };

  lines.forEach((line, index) => {
    if (line.size !== 0) return;
    flushRun(index);
    blocks.push(paragraphType.create(null));
    if (cursorLineIndex === index) cursorBlockIndex = blocks.length - 1;
    runStart = index + 1;
  });
  flushRun(lines.length);

  return { blocks, cursorBlockIndex };
};

/**
 * Predicate gate plus paragraph position math for the regroup above. Returns
 * `null` whenever `setHardBreak` should fall back to the stock command: a
 * non-text or cross-block selection, a textblock that isn't a plain
 * paragraph, a paragraph outside the normalizable contexts (e.g. a table
 * cell), or — the common case — a position where a stock insertion stays
 * representable (mid-line, no adjacent break). The predicate is "would this
 * press leave a break-only line" after any selected range is replaced: at
 * line start (`parentOffset === 0` covers press N ≥ 3, which lands at the
 * start of the previous press's empty paragraph) or directly against a
 * surviving break on either side of the range: `$from.nodeBefore` catches a
 * selection starting right after a break (replacing the text that follows
 * it), `$to.nodeAfter` a selection ending right before one (replacing the
 * text that precedes it) — either replace must not weld a fresh break onto
 * the survivor.
 */
export const planHardBreakLineNormalization = (
  state: EditorState
): HardBreakLineNormalizationPlan | null => {
  const { selection } = state;
  if (!(selection instanceof TextSelection)) return null;

  const { paragraph: paragraphType, hardBreak: hardBreakType } = state.schema.nodes;
  if (!paragraphType || !hardBreakType) return null;

  const { $from, $to } = selection;
  if (!$from.sameParent($to)) return null;
  if ($from.parent.type !== paragraphType) return null;
  // depth 0 would make the doc itself the parent, already excluded above.
  if (!NORMALIZABLE_PARENT_NAMES[$from.node($from.depth - 1).type.name]) return null;

  const isBreakOnlyLine =
    $from.parentOffset === 0 ||
    $from.nodeBefore?.type === hardBreakType ||
    $to.nodeAfter?.type === hardBreakType;
  if (!isBreakOnlyLine) return null;

  return {
    from: $from.before(),
    to: $from.after(),
    ...regroupParagraphAtCursor(
      $from.parent.content,
      $from.parentOffset,
      $to.parentOffset,
      paragraphType,
      hardBreakType
    ),
  };
};

/**
 * Applies a plan: replaces the paragraph with its regrouped blocks and drops
 * the cursor at the content start of `cursorBlockIndex`'s block. `marksToKeep`
 * mirrors stock `setHardBreak`'s `keepMarks` continuation (already filtered to
 * splittable marks by the caller) so active formatting survives a normalized
 * break exactly as it survives a stock one.
 */
export const applyHardBreakLineNormalization = (
  tr: Transaction,
  plan: HardBreakLineNormalizationPlan,
  marksToKeep?: readonly Mark[] | null
): Transaction => {
  const { from, to, blocks, cursorBlockIndex } = plan;
  tr.replaceWith(from, to, Fragment.fromArray(blocks));

  let cursorPos = from;
  for (let index = 0; index < cursorBlockIndex; index += 1) {
    cursorPos += blocks[index]!.nodeSize;
  }
  tr.setSelection(TextSelection.create(tr.doc, cursorPos + 1));

  if (marksToKeep?.length) tr.ensureMarks(marksToKeep as Mark[]);

  return tr;
};

/**
 * HardBreak whose `setHardBreak` never writes an unrepresentable
 * line. Stock Shift-Enter/Mod-Enter both route through this command —
 * as does dictation's programmatic insert — so the interception covers every
 * entry point. A press that would create a break-only line — a run of
 * adjacent breaks, a paragraph-leading break, or a selection replace landing
 * against a surviving break — is rewritten into empty-paragraph structure
 * instead, the `&nbsp;` vocabulary that survives the markdown round trip;
 * every other press falls through to the stock command.
 *
 * No option/plugin/node-view changes: the frozen canonical stack trace
 * (name/kind/priority/options) and the runtime projections (`commandNames`,
 * `pluginKeys`) stay byte-identical to stock `HardBreak`, so both stacks
 * (`buildMarkdownNativeExtensions`, `buildInternalCanonicalStack`) share this
 * one node — like `MarkdownSectionNodeBase`, unlike the still-duplicated
 * pre-5.3 `readOnlyCodeBlock` — without disturbing the D4 parity gates.
 */
export const MarkdownHardBreak = HardBreak.extend({
  addCommands() {
    const parentCommands = this.parent?.();
    return {
      setHardBreak: () => (props) => {
        const plan = planHardBreakLineNormalization(props.state);
        if (!plan) return parentCommands?.setHardBreak?.()(props) ?? false;
        if (props.dispatch) {
          const { storedMarks, selection } = props.state;
          const activeMarks = this.options.keepMarks
            ? (storedMarks ??
              (selection.$to.parentOffset ? selection.$from.marks() : null))
            : null;
          const { splittableMarks } = props.editor.extensionManager;
          applyHardBreakLineNormalization(
            props.tr,
            plan,
            activeMarks?.filter((mark) => splittableMarks.includes(mark.type.name))
          );
        }
        return true;
      },
    };
  },
});
